import { useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowRight, ReceiptText, TriangleAlert } from "lucide-react";

import { orpc } from "@/client";
import { FinanceiroLabels } from "@/lib/labels/financeiro";
import { cn } from "@/lib/utils";

import { CashDayDetails } from "./cash-day-details";
import type { FinanceModal } from "./types";

export function CashDayPanel({
	date,
	onModal,
}: {
	date: string;
	onModal: (modal: FinanceModal) => void;
}) {
	const { data } = useSuspenseQuery(orpc.financeiro.treasury.day.queryOptions({ input: { date } }));
	const [view, setView] = useState<"pending" | "receipts" | null>(null);
	const pendingCount = data.missing_attendances.length + data.unidentified_incomes.length;
	const missingEvidence = data.receipts.some((receipt) => receipt.state === "pending");

	return (
		<>
			<section
				aria-label="Recebimentos do dia"
				className="workspace-frame overflow-hidden @container"
			>
				<div className="grid min-w-0 grid-cols-1 @2xl:grid-cols-[14rem_minmax(0,1fr)]">
					<header className="bg-primary-hover p-5 text-primary-foreground">
						<p className="text-xs font-semibold uppercase tracking-eyebrow text-sidebar-accent">
							Recebido no dia
						</p>
						<p className="mt-3 text-3xl font-semibold leading-none tracking-tight tabular-nums">
							{FinanceiroLabels.money(data.total_cents)}
						</p>
					</header>
					<ul className="grid min-w-0 grid-cols-2 divide-x divide-border @4xl:grid-cols-3">
						{data.totals.map((total) => (
							<li key={total.method} className="min-w-0 border-b border-border px-4 py-3">
								<p className="text-xs text-muted-foreground">
									{FinanceiroLabels.receipt.method[total.method]}
								</p>
								<p className="mt-1 text-base font-semibold tabular-nums text-foreground">
									{FinanceiroLabels.money(total.amount_cents)}
								</p>
								<p className="mt-0.5 text-xs text-muted-foreground">{total.count} recebimentos</p>
							</li>
						))}
					</ul>
				</div>
				<div className="grid grid-cols-1 divide-y divide-border border-t border-border @lg:grid-cols-2 @lg:divide-x @lg:divide-y-0">
					<button
						type="button"
						onClick={() => setView("pending")}
						className={cn(
							"flex min-h-14 min-w-0 items-center gap-3 px-4 py-3 text-left text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
							pendingCount > 0
								? "bg-warning-soft text-warning hover:bg-warning/15"
								: "text-muted-foreground hover:bg-secondary",
						)}
					>
						<TriangleAlert className="size-4 shrink-0" />
						<span>Falta registrar</span>
						<span className="ml-auto tabular-nums">{pendingCount}</span>
						<ArrowRight className="size-4 shrink-0" />
					</button>
					<button
						type="button"
						onClick={() => setView("receipts")}
						className="flex min-h-14 min-w-0 items-center gap-3 px-4 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
					>
						<ReceiptText className="size-4 shrink-0 text-primary" />
						<span className="flex min-w-0 flex-col gap-1">
							<span>Registrados</span>
							{missingEvidence && (
								<span className="text-xs text-warning">Há comprovantes pendentes</span>
							)}
						</span>
						<span className="ml-auto tabular-nums">{data.receipts.length}</span>
						<ArrowRight className="size-4 shrink-0 text-muted-foreground" />
					</button>
				</div>
			</section>
			{view !== null && (
				<CashDayDetails data={data} view={view} onModal={onModal} onClose={() => setView(null)} />
			)}
		</>
	);
}
